import { create } from 'zustand';
import api, { SUBSCRIPTION_EXPIRED } from '../api/client';
import { isTelegram } from '../app/surface';

interface User {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_staff: boolean;
}

export interface Permissions {
  is_super_admin: boolean;
  is_admin: boolean;
  can_see_cost: boolean;
  can_edit_prices: boolean;
  can_give_discount: boolean;
  can_manage_expenses: boolean;
  can_edit_orders: boolean;
  max_discount_percent: string;
  role_name: string | null;
}

/** `orders.view` → `'own' | 'shop'`. Ruxsat yo'q bo'lsa kalit ham yo'q. */
export type PermissionMap = Record<string, 'own' | 'shop'>;

export type SellerStatus = 'pending' | 'approved' | 'rejected' | null;

export interface SellerContext {
  is_seller: boolean;
  seller_id?: number;
  status: SellerStatus;
  has_shop: boolean;
  is_owner?: boolean;
  shop: { id: number; name: string } | null;
  rejection_reason: string;
  /** Do'kon obunasi — do'koni yo'q bo'lsa null. */
  subscription?: SubscriptionBrief | null;
}

export interface SubscriptionBrief {
  active: boolean;
  until: string;
  days_left: number;
}

const DEFAULT_PERMS: Permissions = {
  is_super_admin: false,
  is_admin: false,
  can_see_cost: false,
  can_edit_prices: false,
  can_give_discount: false,
  can_manage_expenses: false,
  can_edit_orders: false,
  max_discount_percent: '0.00',
  role_name: null,
};

const DEFAULT_SELLER: SellerContext = {
  is_seller: false,
  status: null,
  has_shop: false,
  is_owner: false,
  shop: null,
  rejection_reason: '',
};

export interface RegisterInput {
  name: string;
  phone: string;
  username: string;
  password: string;
}

interface AuthState {
  user: User | null;
  permissions: Permissions;
  /** Yangi ruxsat modeli — navigatsiya va tugmalar shundan o'qiydi. */
  rbac: PermissionMap;
  seller: SellerContext;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Kirish rad etilgan sabab (masalan super admin do'kon paneliga urinsa) */
  authNotice: string | null;
  /** Telegram akkaunt hech qaysi hisobga bog'lanmagan — parol formasi kerak. */
  needsTelegramBind: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<string>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshSeller: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: DEFAULT_PERMS,
  rbac: {},
  seller: DEFAULT_SELLER,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  // Telegram'da token yo'q va `checkAuth` javobini kutish kerak. Aks holda
  // birinchi renderda "autentifikatsiya yo'q" deb login sahifasiga otib
  // yuborardi — u yerda esa Telegram foydalanuvchisi uchun hech nima yo'q.
  isLoading: isTelegram(),
  authNotice: null,
  needsTelegramBind: false,

  login: async (username: string, password: string) => {
    set({ isLoading: true });
    // Eski stale token bo'lsa o'chiramiz — yangi loginni 401 ushlab qolmasin
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    try {
      const response = await api.post('/auth/', { username, password, app: 'seller' });
      const { token, user, permissions, rbac, seller } = response.data;

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Token ${token}`;

      set({
        user,
        permissions: permissions || user?.permissions || DEFAULT_PERMS,
        rbac: rbac || {},
        seller: seller || DEFAULT_SELLER,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      set({ isLoading: false });
      const data = error.response?.data || {};
      // Ariza holati (pending/rejected) — kod bilan qaytadi.
      if (data.code === 'pending' || data.code === 'rejected' || data.code === 'super_admin') {
        const err: any = new Error(data.error || 'Kirish rad etildi');
        err.code = data.code;
        err.reason = data.reason;
        throw err;
      }
      const message = data.error || data.detail || 'Login xatosi';
      throw new Error(message);
    }
  },

  register: async (input: RegisterInput) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/auth/register/', input);
      set({ isLoading: false });
      return response.data?.message || 'Ariza qabul qilindi.';
    } catch (error: any) {
      set({ isLoading: false });
      const data = error.response?.data || {};
      // DRF field xatolari: {username: [...], phone: [...]}
      const firstField = Object.keys(data).find((k) => Array.isArray(data[k]));
      const message = firstField
        ? data[firstField][0]
        : data.error || data.detail || 'Ro\'yxatdan o\'tishda xato';
      throw new Error(message);
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    set({
      user: null, permissions: DEFAULT_PERMS, rbac: {},
      seller: DEFAULT_SELLER, token: null, isAuthenticated: false,
    });
  },

  checkAuth: async () => {
    // Telegram ichida token yo'q va kerak ham emas: har bir so'rovga
    // imzolangan initData qo'shiladi va server sotuvchini o'sha orqali
    // taniydi. Shuning uchun tokensiz ham `/auth/` ga boramiz.
    const token = get().token;
    if (!token && !isTelegram()) {
      set({ isAuthenticated: false, isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      const response = await api.get('/auth/');
      set({
        user: response.data,
        permissions: response.data.permissions || DEFAULT_PERMS,
        rbac: response.data.rbac || {},
        seller: response.data.seller || DEFAULT_SELLER,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      const code = error?.response?.data?.code;
      // Telegram akkaunt hisobga bog'lanmagan — login sahifasi emas, bog'lash
      // formasi kerak. Buni `needsTelegramBind` bilan ekranga aytamiz.
      if (code === 'telegram_not_linked') {
        set({
          user: null, permissions: DEFAULT_PERMS, rbac: {},
          seller: DEFAULT_SELLER, isAuthenticated: false, isLoading: false,
          needsTelegramBind: true,
        });
        return;
      }

      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      // Server sababini aytgan bo'lsa (masalan super admin), login sahifasi
      // uni ko'rsatishi kerak — aks holda foydalanuvchi sababsiz chiqarilgandek
      // bo'lardi.
      const data = error?.response?.data || {};
      set({
        user: null,
        permissions: DEFAULT_PERMS,
        rbac: {},
        seller: DEFAULT_SELLER,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        authNotice: data.code === 'super_admin' ? data.error : null,
      });
    }
  },

  refreshSeller: async () => {
    try {
      const response = await api.get('/auth/');
      set({ seller: response.data.seller || DEFAULT_SELLER });
    } catch {
      // jim
    }
  },
}));


/**
 * Ruxsat tekshiruvi — komponentlar shu orqali so'raydi.
 *
 * MUHIM: bu faqat UX. Tugmani yashirish himoya emas — qaror serverda,
 * `HasPermission` da qabul qilinadi.
 */
export const usePermission = (permission: string): boolean =>
  useAuthStore((state) => permission in state.rbac);

/** Ruxsat qamrovi: `'own'`, `'shop'` yoki ruxsat yo'q bo'lsa `null`. */
export const usePermissionScope = (permission: string): 'own' | 'shop' | null =>
  useAuthStore((state) => state.rbac[permission] ?? null);

// Server obuna tugaganini aytdi — holatni yangilaymiz, ProtectedRoute
// foydalanuvchini Obuna bo'limiga olib boradi.
window.addEventListener(SUBSCRIPTION_EXPIRED, () => {
  const { seller } = useAuthStore.getState();
  if (seller.subscription?.active) {
    useAuthStore.setState({ seller: { ...seller, subscription: { ...seller.subscription, active: false } } });
  }
});
